import { Box, Button, SvgIcon, Typography, keyframes } from '@mui/material';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import PropTypes from 'prop-types';

const fadeInUp = keyframes`
  from { opacity: 0; transform: translateY(16px); }
  to { opacity: 1; transform: translateY(0); }
`;

function AdminHubCard({
  title,
  description,
  accent = '#F4C24A',
  Icon,
  onClick,
  index = 0
}) {
  return (
    <Box
      component="button"
      type="button"
      onClick={onClick}
      sx={{
        width: '100%',
        minHeight: { xs: 148, md: 168 },
        p: { xs: 2.5, md: 3 },
        border: '1px solid rgba(255, 255, 255, 0.6)',
        borderRadius: '20px',
        cursor: 'pointer',
        textAlign: 'left',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        background: `linear-gradient(145deg, rgba(255,255,255,0.72) 0%, rgba(255,255,255,0.4) 55%, ${accent}14 100%)`,
        backdropFilter: 'blur(16px) saturate(180%)',
        WebkitBackdropFilter: 'blur(16px) saturate(180%)',
        boxShadow:
          '0 8px 28px rgba(31, 41, 51, 0.07), inset 0 1px 0 rgba(255,255,255,0.85)',
        position: 'relative',
        overflow: 'hidden',
        animation: `${fadeInUp} 0.5s cubic-bezier(0.22, 1, 0.36, 1) both`,
        animationDelay: `${index * 0.07}s`,
        transition:
          'transform 0.4s cubic-bezier(0.34, 1.56, 0.64, 1), box-shadow 0.4s ease, border-color 0.35s ease',
        '&::before': {
          content: '""',
          position: 'absolute',
          left: 0,
          top: 0,
          width: 5,
          height: '100%',
          background: `linear-gradient(180deg, ${accent}, ${accent}99)`,
          borderRadius: '20px 0 0 20px'
        },
        '&::after': {
          content: '""',
          position: 'absolute',
          right: -30,
          top: -30,
          width: 120,
          height: 120,
          borderRadius: '50%',
          background: `radial-gradient(circle, ${accent}25 0%, transparent 70%)`,
          pointerEvents: 'none'
        },
        '&:hover': {
          transform: 'translateY(-8px) scale(1.01)',
          borderColor: 'rgba(255,255,255,0.9)',
          boxShadow: `0 20px 44px ${accent}28, inset 0 1px 0 rgba(255,255,255,1)`,
          '& .hub-card-cta': {
            bgcolor: accent,
            color: '#1F2933',
            borderColor: accent
          },
          '& .hub-card-icon': {
            transform: 'scale(1.08)',
            boxShadow: `0 8px 20px ${accent}35`
          }
        }
      }}
    >
      <Box sx={{ position: 'relative', zIndex: 1 }}>
        <Box
          className="hub-card-icon"
          sx={{
            width: 52,
            height: 52,
            borderRadius: '14px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mb: 2,
            bgcolor: 'rgba(255,255,255,0.75)',
            border: `1.5px solid ${accent}35`,
            transition: 'transform 0.4s ease, box-shadow 0.4s ease'
          }}
        >
          <SvgIcon sx={{ fontSize: 28 }}>
            <Icon color="#1F2933" />
          </SvgIcon>
        </Box>
        <Typography
          sx={{
            fontWeight: 800,
            fontSize: { xs: '1.1rem', md: '1.25rem' },
            color: '#1F2933',
            letterSpacing: '-0.02em',
            mb: 0.75
          }}
        >
          {title}
        </Typography>
        {description && (
          <Typography
            sx={{
              fontSize: '0.875rem',
              fontWeight: 500,
              color: '#4E565F',
              lineHeight: 1.45,
              maxWidth: 280
            }}
          >
            {description}
          </Typography>
        )}
      </Box>
      <Button
        className="hub-card-cta"
        endIcon={<ArrowForwardRoundedIcon sx={{ fontSize: 18 }} />}
        sx={{
          position: 'relative',
          zIndex: 1,
          alignSelf: 'flex-start',
          mt: 2.5,
          textTransform: 'none',
          fontWeight: 700,
          fontSize: '0.875rem',
          borderRadius: '10px',
          px: 2,
          py: 0.75,
          bgcolor: 'rgba(255,255,255,0.85)',
          color: '#1F2933',
          border: '1px solid #E4E7EB',
          boxShadow: 'none',
          transition: 'all 0.35s ease',
          pointerEvents: 'none'
        }}
      >
        Open
      </Button>
    </Box>
  );
}

AdminHubCard.propTypes = {
  title: PropTypes.string.isRequired,
  description: PropTypes.string,
  accent: PropTypes.string,
  Icon: PropTypes.elementType.isRequired,
  onClick: PropTypes.func.isRequired,
  index: PropTypes.number
};

export default AdminHubCard;
